import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  FormField,
  QueryStateBoundary,
  EmptyState,
} from "@/components/ui";
import { useInquiries } from "@/api/hooks/useInquiries";
import { EVENT_TYPE_LABELS, STAGE_LABELS, type Inquiry } from "@/api/types/inquiry";

interface InquiryPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (inquiryId: string) => void;
}

/**
 * Fallback entry point for "start a quote": search inquiries and pick one.
 * (The primary path is the "Create quote" button on the inquiry detail
 * dialog itself — this covers the plan's "or add quotes as a section that
 * lets you start one by picking an inquiry" alternative.)
 */
export function InquiryPickerDialog({ open, onClose, onSelect }: InquiryPickerDialogProps) {
  const [q, setQ] = useState("");
  const { data, isLoading, error, refetch } = useInquiries({ q, limit: 20 });
  const items = data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Pick an inquiry">
      <DialogContent size="lg">
        <DialogHeader onClose={onClose}>Start a quote from an inquiry</DialogHeader>
        <DialogBody className="space-y-4">
          <FormField
            label="Search inquiries"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Contact name, venue..."
          />

          <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
            {items.length === 0 ? (
              <EmptyState
                icon="🔍"
                title="No inquiries found"
                description="Create an inquiry from the pipeline first."
              />
            ) : (
              <ul className="divide-y divide-border">
                {items.map((item: Inquiry) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(item.id)}
                      className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-3 text-left hover:bg-bg-hover"
                    >
                      <span className="font-medium text-text-primary">{item.contact_name}</span>
                      <span className="flex items-center gap-2 text-xs text-text-muted">
                        {item.event_type && EVENT_TYPE_LABELS[item.event_type]}
                        {item.guest_count != null ? ` · ${item.guest_count} guests` : ""}
                        {` · ${STAGE_LABELS[item.stage]}`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </QueryStateBoundary>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
